import { useParams, useNavigate } from 'react-router-dom';
import { useTelemedicineSession } from '@/hooks/telemedicine';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/Button';

export function ConsultationSummaryPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { data: session, isLoading } = useTelemedicineSession(sessionId);
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR';

  if (isLoading) {
    return (
      <div className="container max-w-3xl py-8 text-center text-gray-500">
        Carregando resumo...
      </div>
    );
  }

  if (!session) {
    return (
      <div className="container max-w-3xl py-8 text-center text-gray-500">
        Sessão não encontrada
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Resumo da Consulta</h1>
        <p className="text-gray-500 mt-1">
          Sessão #{sessionId?.substring(0, 8)} · {new Date(session.scheduledAt).toLocaleString('pt-BR')}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <SummaryCard label="Status" value={session.status} />
          <SummaryCard
            label="Duração"
            value={
              session.startedAt && session.endedAt
                ? `${Math.floor(
                    (new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 60000,
                  )} min`
                : 'Em andamento'
            }
          />
          <SummaryCard
            label="Recurso"
            value={session.enableRecording ? 'Habilitado' : 'Desabilitado'}
          />
        </div>

        <div>
          <h2 className="font-semibold text-gray-900 mb-2">Recursos disponíveis</h2>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            <li>Chat: {session.enableChat ? '✓' : '✗'}</li>
            <li>Compartilhamento de tela: {session.enableScreenShare ? '✓' : '✗'}</li>
            <li>Gravação: {session.enableRecording ? '✓' : '✗'}</li>
          </ul>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">Próximos passos</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Registro médico será criado automaticamente pelo sistema</li>
            <li>Resumo IA será gerado para auxiliar o médico</li>
            <li>Você receberá a prescrição e o atestado (se aplicável) por aqui</li>
            <li>Avaliação da consulta poderá ser feita após 24h</li>
          </ul>
        </div>

        <Button variant="outline" onClick={() => navigate(isDoctor ? '/medico/dashboard' : '/dashboard')}>
          Voltar ao Dashboard
        </Button>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-bold text-gray-900 mt-1">{value}</div>
    </div>
  );
}